import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface WeeklyEarning {
  id: string;
  user_id: string;
  week_start: string;
  coins_earned: number;
  updated_at: string;
  // Joined profile data
  username?: string;
  display_name?: string;
  avatar_url?: string;
  avatar_type?: string;
  avatar_config?: any;
}

export interface LeaderboardEntry extends WeeklyEarning {
  rank: number;
}

// Prize tiers for weekly leaderboard
export const WEEKLY_PRIZES = [
  { rank: 1, coins: 1000, gems: 50, label: '🥇 Champion' },
  { rank: 2, coins: 500, gems: 25, label: '🥈 Runner Up' },
  { rank: 3, coins: 250, gems: 10, label: '🥉 Third Place' },
  { rank: 4, coins: 100, gems: 0, label: '4th Place' },
  { rank: 5, coins: 100, gems: 0, label: '5th Place' },
  { rank: 6, coins: 50, gems: 0, label: '6th-10th' },
  { rank: 10, coins: 50, gems: 0, label: '6th-10th' },
];

export function useWeeklyLeaderboard() {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [userStats, setUserStats] = useState<{ rank: number; coinsEarned: number } | null>(null);
  const [previousWinners, setPreviousWinners] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Get the start of the current week (Monday)
  const getWeekStart = (date: Date = new Date()): string => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    const monday = new Date(d.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday.toISOString().split('T')[0];
  };

  // Get time until next Sunday midnight
  const getTimeUntilReset = (): { days: number; hours: number; minutes: number; seconds: number } => {
    const now = new Date();
    const sunday = new Date(now);
    sunday.setDate(now.getDate() + (7 - now.getDay()));
    sunday.setHours(23, 59, 59, 999);
    
    const diff = sunday.getTime() - now.getTime();
    
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((diff % (1000 * 60)) / 1000),
    };
  };

  const fetchCurrentWeekLeaderboard = async () => {
    const weekStart = getWeekStart();
    
    const { data, error } = await supabase
      .from('weekly_coin_earnings')
      .select(`
        *,
        user_profiles:user_id (
          username,
          display_name,
          avatar_url,
          avatar_type,
          avatar_config
        )
      `)
      .eq('week_start', weekStart)
      .order('coins_earned', { ascending: false })
      .limit(50);

    if (!error && data) {
      const entries: LeaderboardEntry[] = data.map((entry: any, index: number) => ({
        ...entry,
        username: entry.user_profiles?.username,
        display_name: entry.user_profiles?.display_name,
        avatar_url: entry.user_profiles?.avatar_url,
        avatar_type: entry.user_profiles?.avatar_type,
        avatar_config: entry.user_profiles?.avatar_config,
        rank: index + 1,
      }));
      
      setLeaderboard(entries);

      // Find current user's stats
      if (user) {
        const userEntry = entries.find(e => e.user_id === user.id);
        if (userEntry) {
          setUserStats({ rank: userEntry.rank, coinsEarned: userEntry.coins_earned });
        } else {
          // User not in top 50, fetch their specific stats
          const { data: userData } = await supabase
            .from('weekly_coin_earnings')
            .select('coins_earned')
            .eq('user_id', user.id)
            .eq('week_start', weekStart)
            .single();

          if (userData) {
            // Count how many users are ahead
            const { count } = await supabase
              .from('weekly_coin_earnings')
              .select('*', { count: 'exact', head: true })
              .eq('week_start', weekStart)
              .gt('coins_earned', userData.coins_earned);

            setUserStats({ rank: (count || 0) + 1, coinsEarned: userData.coins_earned });
          } else {
            setUserStats({ rank: 0, coinsEarned: 0 });
          }
        }
      }
    }
  };

  const fetchPreviousWeekWinners = async () => {
    // Get previous week start
    const now = new Date();
    now.setDate(now.getDate() - 7);
    const prevWeekStart = getWeekStart(now);

    const { data, error } = await supabase
      .from('weekly_coin_earnings')
      .select(`
        *,
        user_profiles:user_id (
          username,
          display_name,
          avatar_url,
          avatar_type,
          avatar_config
        )
      `)
      .eq('week_start', prevWeekStart)
      .order('coins_earned', { ascending: false })
      .limit(3);

    if (!error && data) {
      const entries: LeaderboardEntry[] = data.map((entry: any, index: number) => ({
        ...entry,
        username: entry.user_profiles?.username,
        display_name: entry.user_profiles?.display_name,
        avatar_url: entry.user_profiles?.avatar_url,
        avatar_type: entry.user_profiles?.avatar_type,
        avatar_config: entry.user_profiles?.avatar_config,
        rank: index + 1,
      }));
      
      setPreviousWinners(entries);
    }
  };

  const getPrizeForRank = (rank: number) => {
    if (rank === 1) return WEEKLY_PRIZES[0];
    if (rank === 2) return WEEKLY_PRIZES[1];
    if (rank === 3) return WEEKLY_PRIZES[2];
    if (rank === 4) return WEEKLY_PRIZES[3];
    if (rank === 5) return WEEKLY_PRIZES[4];
    if (rank >= 6 && rank <= 10) return WEEKLY_PRIZES[5];
    return null;
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([
        fetchCurrentWeekLeaderboard(),
        fetchPreviousWeekWinners()
      ]);
      setLoading(false);
    };
    init();
  }, [user]);

  return {
    leaderboard,
    userStats,
    previousWinners,
    loading,
    getTimeUntilReset,
    getWeekStart,
    getPrizeForRank,
    refreshLeaderboard: fetchCurrentWeekLeaderboard,
  };
}
